import { useRef, useState } from 'react';
import { SystemRole, hasMinimumRole, unresolvedPlaceholder } from '@peles/shared';
import type { RenderedSavedReply } from '@peles/shared';
import { RichTextEditor } from '../common';
import { useAuth } from '../../context/AuthContext';
import type { MergeContext, MergeRecord } from '../../api/savedReplies';
import { WritingAssistant } from './WritingAssistant';
import { SavedReplyPicker } from './SavedReplyPicker';
import { SavedReplyModal } from './SavedReplyModal';
import { htmlToPlainText, plainTextToHtml } from './composeEmailUtils';

export interface MessageComposerProps {
  /** The draft: HTML when `format` is `html`, plain text otherwise. */
  value: string;
  onChange: (value: string) => void;
  /**
   * `html` for an email body (the rich-text editor), `text` for everything
   * that is not markup — a conversation, a text message, a note.
   */
  format?: 'html' | 'text';
  /** Ties the label to the field, and keeps the field findable by it. */
  id: string;
  label?: string;
  /** The rich editor's accessible name, when it should differ from `label`. */
  ariaLabel?: string;
  placeholder?: string;
  /** Rows of the plain editor. */
  rows?: number;
  /** A hard limit, held for inserted and suggested text as well as typing. */
  maxLength?: number;
  disabled?: boolean;
  /**
   * The record(s) the message is about, whose details fill a saved reply's
   * tags — the event, and the one vendor it goes to when there is one.
   */
  record?: MergeContext;
  /**
   * The message's subject, when it has one. A saved reply's subject fills it
   * only while it is still empty — never over what somebody typed.
   */
  subject?: string;
  onSubjectChange?: (subject: string) => void;
  /** The AI writing assistant (grammar, tone, translate). Default on. */
  assistant?: boolean;
  /** "Saved replies" beside the label. Default on; offered to producers only. */
  savedReplies?: boolean;
  /**
   * Told when a saved reply has gone into the draft, with the records whose
   * details it actually used — so a host whose recipients can change
   * afterwards knows the text names somebody it may no longer only go to.
   */
  onReplyInserted?: (filledFrom: MergeRecord[]) => void;
  /** Where errors go when the host already shows them; otherwise shown here. */
  onError?: (message: string) => void;
}

const TEXTAREA_CLASS =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50';

/**
 * The one message body the application writes in.
 *
 * Every place a producer writes something that leaves the office — an email,
 * the opening of a conversation, a guest broadcast, a request for a file —
 * renders this, so each gets the same things: the writing assistant, the saved
 * replies with their merge tags filled from the record, and "save this as a
 * reply". What is around the body (recipients, attachments, a channel) stays
 * the host's, because that is what differs between them.
 *
 * Inserting a reply appends to the draft rather than replacing it, and fills
 * an empty draft outright: what was already written is the writer's.
 */
export function MessageComposer({
  value,
  onChange,
  format = 'text',
  id,
  label,
  ariaLabel,
  placeholder,
  rows = 5,
  maxLength,
  disabled,
  record,
  subject,
  onSubjectChange,
  assistant = true,
  savedReplies = true,
  onReplyInserted,
  onError,
}: MessageComposerProps) {
  const { user } = useAuth();
  const [localError, setLocalError] = useState<string | null>(null);
  const [unresolved, setUnresolved] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const isHtml = format === 'html';
  const plain = isHtml ? htmlToPlainText(value) : value;
  const offerReplies =
    savedReplies && user != null && hasMinimumRole(user.systemRole, SystemRole.PLANNER);

  function report(message: string) {
    if (onError) onError(message);
    else setLocalError(message);
  }

  /**
   * The draft and its subject as they are *now*, for a reply that lands after
   * a round trip. Read from the render that chose it, a reply would be added
   * to the draft of that moment and erase whatever was typed while the server
   * filled it in — or fill a subject typed in the meantime.
   */
  const latest = useRef({ value, subject, onChange, onSubjectChange, onReplyInserted });
  latest.current = { value, subject, onChange, onSubjectChange, onReplyInserted };

  /** Apply `next` unless it breaks the limit, which the textarea holds only for typing. */
  function apply(next: string): boolean {
    if (maxLength !== undefined && next.length > maxLength) {
      report(
        `That would take the message past its ${maxLength.toLocaleString('en-US')}-character limit. Shorten it first.`,
      );
      return false;
    }
    latest.current.onChange(next);
    return true;
  }

  function insert(reply: RenderedSavedReply) {
    const now = latest.current;
    const current = now.value;
    const hasText = (isHtml ? htmlToPlainText(current) : current).trim() !== '';
    const next = isHtml
      ? `${hasText ? current : ''}${plainTextToHtml(reply.body)}`
      : hasText
        ? `${current.replace(/\s+$/, '')}\n\n${reply.body}`
        : reply.body;
    setLocalError(null);
    if (!apply(next)) return;
    setUnresolved(reply.unresolved);
    if (reply.subject && now.onSubjectChange && !now.subject?.trim()) {
      now.onSubjectChange(reply.subject);
    }
    now.onReplyInserted?.(reply.filledFrom);
  }

  function accept(text: string) {
    apply(isHtml ? plainTextToHtml(text) : text);
  }

  return (
    <div data-testid={`composer-${id}`}>
      <div className="flex items-end justify-between gap-2 mb-1">
        {label && isHtml ? (
          // The rich editor names itself with `ariaLabel`; there is no input to point at.
          <span className="block text-sm font-medium text-gray-700">{label}</span>
        ) : label ? (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        ) : (
          <span />
        )}
        {offerReplies && (
          <SavedReplyPicker
            record={record}
            onInsert={insert}
            onSaveDraft={plain.trim() ? () => setSaving(true) : undefined}
            disabled={disabled}
            onError={report}
          />
        )}
      </div>

      {isHtml ? (
        <RichTextEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          ariaLabel={ariaLabel ?? label ?? 'Message'}
          disabled={disabled}
        />
      ) : (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          className={TEXTAREA_CLASS}
        />
      )}

      {unresolved.length > 0 && (
        <p className="mt-1 text-xs text-amber-700" data-testid="composer-unresolved">
          Fill in {unresolved.map(unresolvedPlaceholder).join(', ')} before sending.
          <button
            type="button"
            onClick={() => setUnresolved([])}
            className="ml-2 underline hover:text-amber-900"
          >
            Got it
          </button>
        </p>
      )}

      {assistant && (
        <div className="mt-2">
          <WritingAssistant
            text={plain}
            revision={value}
            onAccept={accept}
            disabled={disabled}
            onError={report}
          />
        </div>
      )}

      {localError && (
        <p className="mt-1 text-xs text-red-600" role="alert" data-testid="composer-error">
          {localError}
        </p>
      )}

      {offerReplies && (
        <SavedReplyModal
          open={saving}
          onClose={() => setSaving(false)}
          seed={{ subject: subject ?? null, body: plain }}
        />
      )}
    </div>
  );
}
